// src/modules/Beneficiaries.js
import * as React from 'react';
import {
  Datagrid,
  List,
  TextField,
  DateField,
  BooleanField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  ReferenceInput,
  BooleanInput,
  DateInput,
  NumberInput,
  Create,
  Edit,
  SimpleForm,
  required,
  usePermissions,
  useListContext,
  downloadCSV,
  useRecordContext,
  Button,
  useUpdate,
  useNotify,
  useRefresh,
  ChipField,
  SingleFieldList,
  ArrayField,
  SelectArrayInput,
  ReferenceArrayInput,
  PasswordInput,
  regex,
  TopToolbar,
  CreateButton,
  ExportButton,
  Show,
  ShowButton,
  DeleteButton,
  EditButton
} from "react-admin";
import { Fragment, useState } from "react";
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  Stack, 
  TablePagination,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Link
} from "@mui/material";
import { unparse as convertToCSV } from 'papaparse';
import jsonExport from 'jsonexport/dist';
import BulkUploadBeneficiaries from "./components/bulk-upload-beneficiaries";
import BeneficiarySmsButton from "./components/beneficiary-sms-dialog";
import { BulkSmsSelectedAction, BulkSmsByFilterButton } from "./components/bulk-sms-beneficiaries";

const PHONE_REGEX = /^\+249\d{9}$/;

// Custom List Actions without external component
const BeneficiaryListActions = () => (
  <TopToolbar>
    <CreateButton />
    <BulkUploadBeneficiaries />
    <BulkSmsByFilterButton />
    <ExportButton />
  </TopToolbar>
);

// Define a custom filter component for beneficiaries
export const BeneficiariesFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
    <TextInput label="Phone Number" source="phonenumber" />
    <SelectInput
      source="gender"
      choices={[
        { id: "MALE", name: "Male" },
        { id: "FEMALE", name: "Female" },
        { id: "OTHER", name: "Other" },
      ]}
      optionText="name"
      optionValue="id"
      allowEmpty
    />
    <NumberInput source="state_id" label="State ID" />
    <NumberInput source="locality_id" label="Locality ID" />
    <TextInput label="Field Coordinator" source="fc_name" />
    <BooleanInput source="is_registered" label="Is Registered" />
    <BooleanInput source="is_active" label="Is Active" />
    <DateInput source="fromDate" label="Created From" />
    <DateInput source="toDate" label="Created To" />
  </Filter>
);

const customExporter = (records) => {
  const csvData = records.map(record => ({
    ID: record.id,
    Name: record.name,
    PhoneNumber: record.phonenumber ? `'${record.phonenumber}` : "",
    StateID: record.state_id,
    LocalityID: record.locality_id,
    Gender: record.gender,
    DateOfBirth: record.dob ? new Date(record.dob).toLocaleDateString() : "",
    FieldCoordinator: record.fc_name || "",
    Crops: record.crops || "",
    CreatedAt: record.created_at ? new Date(record.created_at).toLocaleDateString() : "",
    UpdatedAt: record.updated_at ? new Date(record.updated_at).toLocaleDateString() : "",
    IsRegistered: record.is_registered ? "Yes" : "No",
    RegisteredUserID: record.registered_user_id || "",
    IsActive: record.is_active ? "Yes" : "No",
  }));

  jsonExport(csvData, { rowDelimiter: ',' }, (err, csv) => {
    if (err) {
      console.error(err);
      return;
    }
    const bom = '\uFEFF';
    downloadCSV(bom + csv, 'beneficiaries_export');
  });
};

const CustomPagination = (props) => {
  const { total, perPage, page, setPerPage, setPage } = useListContext();

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <TablePagination
      count={total}
      page={page - 1}
      rowsPerPage={perPage}
      onPageChange={(event, newPage) => setPage(newPage + 1)}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={[10, 25, 50, 100]}
      component="div"
    />
  );
};

// Status Chip Component
const StatusChip = ({ is_active, is_registered }) => {
  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {is_active ? (
        <Chip 
          label="Active" 
          size="small" 
          color="success" 
          variant="outlined"
        />
      ) : (
        <Chip 
          label="Inactive" 
          size="small" 
          color="error" 
          variant="outlined"
        />
      )}
      {is_registered && (
        <Chip 
          label="Registered" 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )}
    </Box>
  );
};

// Age Calculation Function
const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Quick Actions Component
const QuickActions = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();

  const handleStatusToggle = async (newStatus) => {
    try {
      await update(
        'beneficiaries',
        { id: record.id, data: { is_active: newStatus } },
        {
          onSuccess: () => {
            notify(`Beneficiary ${newStatus ? 'activated' : 'deactivated'} successfully`, { type: 'success' });
            refresh();
          },
          onError: (error) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  if (!record || !['admin', 'operater'].includes(permissions)) return null;

  return (
    <Box display="flex" gap={1}>
      {record.is_active ? (
        <Button
          label="Deactivate"
          size="small"
          color="warning"
          onClick={() => handleStatusToggle(false)}
          disabled={isLoading}
        />
      ) : (
        <Button
          label="Activate"
          size="small"
          color="success"
          onClick={() => handleStatusToggle(true)}
          disabled={isLoading}
        />
      )}
    </Box>
  );
};

export const BeneficiariesList = (props) => {
  const { permissions } = usePermissions();
  const canEdit = ['admin', 'operater'].includes(permissions);

  return (
    <List
      {...props}
      storeKey={false}
      filters={<BeneficiariesFilter />}
      actions={<BeneficiaryListActions />}
      pagination={<CustomPagination />}
      exporter={customExporter}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      <Datagrid
        rowClick={canEdit ? "edit" : "show"}
        bulkActionButtons={canEdit ? <BulkSmsSelectedAction /> : false}
        sx={{
          "& .column-age": {
            minWidth: 60,
          },
          "& .column-status": {
            minWidth: 120,
          },
          "& .column-actions": {
            minWidth: 150,
          },
        }}
      >
        <TextField source="name" label="Name" />
        <TextField source="phonenumber" label="Phone" />
        <TextField source="gender" label="Gender" />
        <FunctionField
          label="Age"
          render={(record) => calculateAge(record.dob)}
          className="column-age"
        />
        <TextField source="state_id" label="State ID" />
        <TextField source="locality_id" label="Locality ID" />
        <TextField source="fc_name" label="Field Coordinator" />
        <FunctionField
          label="Status"
          render={(record) => (
            <StatusChip 
              is_active={record.is_active}
              is_registered={record.is_registered}
            />
          )}
          className="column-status"
        />
        <DateField source="created_at" label="Created" showTime />
        {/* <FunctionField
          label="Actions"
          render={(record) => <QuickActions record={record} />}
          className="column-actions"
        /> */}
        <BeneficiarySmsButton />
        <ShowButton />
        {canEdit && <EditButton />}
      </Datagrid>
    </List>
  );
};

// Dynamic form fields for create/edit
const BeneficiaryDynamicFields = ({ isEdit = false }) => {
  return (
    <Fragment>
      <TextInput
        source="name"
        label="Name"
        fullWidth
        validate={[required()]}
      />
      <TextInput
        source="phonenumber"
        label="Phone Number"
        fullWidth
        helperText="Format: +249 followed by 9 digits, e.g. +249911234567"
        validate={[required(), regex(PHONE_REGEX, 'Format: +249 followed by 9 digits')]}
      />
      {isEdit && (
        <PasswordInput
          source="password"
          label="New Password"
          fullWidth
          helperText="Leave blank to keep the current password"
        />
      )}
      <SelectInput
        source="gender"
        label="Gender"
        choices={[
          { id: "male", name: "Male" },
          { id: "female", name: "Female" },
        ]}
        fullWidth
        validate={[required()]}
      />
      <DateInput
        source="dob"
        label="Date of Birth"
        fullWidth
      />
      <ReferenceInput source="state_id" reference="state" label="State">
        <SelectInput optionText="name" fullWidth validate={[required()]} />
      </ReferenceInput>
      <ReferenceInput source="locality_id" reference="locality" label="Locality">
        <SelectInput optionText="name" fullWidth />
      </ReferenceInput>
      <TextInput
        source="fc_name"
        label="Field Coordinator Name"
        fullWidth
      />
      <ReferenceArrayInput source="crop_ids" reference="sub_category" label="Crops">
        <SelectArrayInput optionText="name" fullWidth />
      </ReferenceArrayInput>
      <ReferenceArrayInput source="service_ids" reference="servicetypes" label="Services">
        <SelectArrayInput optionText="name" fullWidth />
      </ReferenceArrayInput>
      <TextInput
        source="crops"
        label="Crops (Text)"
        fullWidth
        multiline
        rows={3}
      />
      {!isEdit && (
        <BooleanInput
          source="is_active"
          label="Is Active"
          fullWidth
        />
      )}
    </Fragment>
  );
};

// Create form for beneficiaries
export const BeneficiaryCreate = (props) => {
  const { permissions } = usePermissions();
  const canCreate = ['admin', 'operater'].includes(permissions);

  const transform = (data) => {
    const transformed = {
      ...data,
      state_id: Number(data.state_id),
      locality_id: data.locality_id ? Number(data.locality_id) : null,
      dob: data.dob ? new Date(data.dob).toISOString().split('T')[0] : null,
      is_active: data.is_active || false,
      is_registered: false,
    };
    return transformed;
  };

  return canCreate ? (
    <Create {...props} transform={transform}>
      <SimpleForm>
        <BeneficiaryDynamicFields />
      </SimpleForm>
    </Create>
  ) : undefined;
};

// Read-only registration/status summary shown on the edit form
const BeneficiaryEditStatus = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Status
      </Typography>
      <StatusChip
        is_active={record?.is_active}
        is_registered={record?.is_registered}
      />
    </Box>
  );
};

// Only these fields are accepted by PATCH /beneficiaries/:id
const buildUpdatePayload = (data) => {
  const payload = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.phonenumber !== undefined) payload.phonenumber = data.phonenumber;
  if (data.password) payload.password = data.password;
  if (data.state_id !== undefined && data.state_id !== null && data.state_id !== '') {
    payload.state_id = Number(data.state_id);
  }
  if (data.locality_id !== undefined) {
    payload.locality_id = data.locality_id ? Number(data.locality_id) : null;
  }
  if (data.gender !== undefined) payload.gender = data.gender;
  if (data.dob !== undefined) {
    payload.dob = data.dob ? new Date(data.dob).toISOString().split('T')[0] : null;
  }
  if (data.crop_ids !== undefined) {
    payload.crop_ids = (data.crop_ids || []).map(Number);
  }
  if (data.service_ids !== undefined) {
    payload.service_ids = (data.service_ids || []).map(Number);
  }
  if (data.fc_name !== undefined) payload.fc_name = data.fc_name;
  if (data.crops !== undefined) payload.crops = data.crops;
  return payload;
};

// Edit form for beneficiaries
export const BeneficiaryEdit = (props) => {
  const { permissions } = usePermissions();
  const canEdit = ['admin', 'operater'].includes(permissions);

  return canEdit ? (
    <Edit {...props} transform={buildUpdatePayload} mutationMode="pessimistic">
      <SimpleForm>
        <BeneficiaryDynamicFields isEdit />
        <BeneficiaryEditStatus />
      </SimpleForm>
    </Edit>
  ) : undefined;
};

// Show view for beneficiaries
const BeneficiaryShowContent = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const canEdit = ['admin', 'operater'].includes(permissions);

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
            {record?.phonenumber && (
              <Link
                href={`/#/sms-delivery-reports?filter=${encodeURIComponent(
                  JSON.stringify({ to: record.phonenumber })
                )}&displayedFilters=${encodeURIComponent(JSON.stringify({ to: true }))}`}
                variant="body2"
                underline="hover"
              >
                View SMS history
              </Link>
            )}
            <BeneficiarySmsButton />
            {canEdit && <EditButton />}
          </Box>
          <Box>
            <Typography variant="h5" gutterBottom>
              Basic Information
            </Typography>
            <Typography variant="body1"><strong>Name:</strong> {record?.name}</Typography>
            <Typography variant="body1"><strong>Phone:</strong> {record?.phonenumber}</Typography>
            <Typography variant="body1"><strong>Gender:</strong> {record?.gender}</Typography>
            <Typography variant="body1"><strong>Date of Birth:</strong> {record?.dob ? new Date(record.dob).toLocaleDateString() : 'N/A'}</Typography>
            <Typography variant="body1"><strong>Age:</strong> {calculateAge(record?.dob)} years</Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Location Information
            </Typography>
            <Typography variant="body1"><strong>State ID:</strong> {record?.state_id}</Typography>
            <Typography variant="body1"><strong>Locality ID:</strong> {record?.locality_id || 'N/A'}</Typography>
            <Typography variant="body1"><strong>Field Coordinator:</strong> {record?.fc_name || 'N/A'}</Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Crops &amp; Services
            </Typography>
            <Typography variant="body1"><strong>Crop IDs:</strong> {record?.crop_ids?.join(', ') || 'N/A'}</Typography>
            <Typography variant="body1"><strong>Service IDs:</strong> {record?.service_ids?.join(', ') || 'N/A'}</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              <strong>Crops:</strong> {record?.crops || 'N/A'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Status Information
            </Typography>
            <StatusChip
              is_active={record?.is_active}
              is_registered={record?.is_registered}
            />
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Timestamps
            </Typography>
            <Typography variant="body1"><strong>Created:</strong> {record?.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const BeneficiaryShow = (props) => {
  return (
    <Show {...props} title="Beneficiary Details">
      <BeneficiaryShowContent />
    </Show>
  );
};

// Simple version if you want to quickly test
export const SimpleBeneficiariesList = (props) => {
  return (
    <List {...props}>
      <Datagrid>
        <TextField source="name" />
        <TextField source="phonenumber" />
        <TextField source="gender" />
        <TextField source="state_id" />
        <BooleanField source="is_active" />
        <DateField source="created_at" />
        <ShowButton />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const ListActions = ({ filters, ...props }) => {
  return (
    <TopToolbar>
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box>
          {filters && React.cloneElement(filters, { context: 'button' })}
        </Box>
        <Box>
          <CreateButton />
          <ExportButton />
        </Box>
      </Box>
    </TopToolbar>
  );
};